import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  requestAccountErasure,
  cancelAccountErasure,
  executeAccountErasure,
  getMyDeletionRequest,
} from "@/lib/deletion.functions";
import { exportMyData } from "@/lib/compliance.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BRAND } from "@/lib/brand";

export const Route = createFileRoute("/_authenticated/account/delete")({
  head: () => ({
    meta: [
      { title: `Delete your account — ${BRAND.name}` },
      { name: "description", content: "Request permanent deletion of your account and personal data under GDPR Article 17." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DeleteAccountPage,
});

type RequestRow = NonNullable<Awaited<ReturnType<typeof getMyDeletionRequest>>["request"]>;

function DeleteAccountPage() {
  const navigate = useNavigate();
  const submit = useServerFn(requestAccountErasure);
  const cancel = useServerFn(cancelAccountErasure);
  const execute = useServerFn(executeAccountErasure);
  const getReq = useServerFn(getMyDeletionRequest);
  const exportData = useServerFn(exportMyData);

  const [request, setRequest] = useState<RequestRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getReq();
      setRequest(r.request as RequestRow | null);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acknowledged) { toast.error("Please confirm you understand the consequences."); return; }
    setBusy(true);
    try {
      const r = await submit({ data: { password, reason: reason.trim() || undefined } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Verification email sent. Check your inbox to confirm.");
      setPassword(""); setReason("");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setBusy(false); }
  };

  const onCancel = async () => {
    setBusy(true);
    try {
      const r = await cancel();
      if ("error" in r) throw new Error(r.error);
      toast.success("Deletion request cancelled.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setBusy(false); }
  };

  const onExecute = async () => {
    if (!confirm("This will permanently scrub your personal data and lock your account. Proceed?")) return;
    setBusy(true);
    try {
      const r = await execute();
      if ("error" in r) throw new Error(r.error);
      toast.success("Account erased. You will be signed out.");
      setTimeout(() => navigate({ to: "/", replace: true }), 1500);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setBusy(false); }
  };

  const onExport = async () => {
    setBusy(true);
    try {
      const r = await exportData();
      const blob = new Blob([r.json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `hospitality-reserve-data-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-2xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Account</p>
        <h1 className="mt-3 font-display text-3xl">Delete your account</h1>
        <span className="gold-divider mt-4" />

        <div className="mt-6 border border-border bg-card p-5 text-sm space-y-3">
          <p>
            Under GDPR Article 17 you can request permanent deletion of your personal data. We will:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>Anonymise your profile (name, contact details, address, date of birth) and avatar.</li>
            <li>Scrub your messages from chat threads (the row remains for thread integrity).</li>
            <li>Lock your account permanently — you will not be able to sign back in.</li>
            <li>
              <strong>Retain</strong> financial records (bookings, payouts, receipts, Stripe identifiers) for 6 years to meet our HMRC obligations.
            </li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Need a copy of your data first? <button onClick={onExport} disabled={busy} className="underline text-accent">Download a JSON export</button>.
          </p>
        </div>

        {loading ? (
          <p className="mt-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : request && ["verification_sent", "verified", "scheduled"].includes(request.status) ? (
          <div className="mt-6 border border-accent bg-accent/5 p-5 text-sm space-y-3">
            <p className="tracking-wide-luxury text-accent text-xs">Status: {request.status.replace("_", " ").toUpperCase()}</p>
            {request.status === "verification_sent" && (
              <p>We sent a verification email to your address. Click the link inside to confirm the request.</p>
            )}
            {(request.status === "verified" || request.status === "scheduled") && request.scheduled_deletion_at && (
              <>
                <p>
                  Your account is scheduled for deletion on{" "}
                  <strong>{new Date(request.scheduled_deletion_at).toLocaleString("en-GB")}</strong>.
                </p>
                <p className="text-xs text-muted-foreground">
                  You can cancel any time before then. After the scheduled time you (or our automated job) can finalise it.
                </p>
                {Date.now() >= new Date(request.scheduled_deletion_at).getTime() && (
                  <Button onClick={onExecute} disabled={busy}
                    className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90 tracking-wide-luxury">
                    Finalise deletion now
                  </Button>
                )}
              </>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel} disabled={busy} className="rounded-none tracking-wide-luxury">
                Cancel deletion request
              </Button>
              <Link to="/account" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">
                Back to account
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 border border-border bg-card p-5 space-y-4">
            <p className="text-xs tracking-wide-luxury text-destructive">This action requires identity confirmation.</p>
            <div>
              <Label htmlFor="reason">Reason (optional — helps us improve)</Label>
              <Textarea id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 2000))}
                className="rounded-none mt-1" placeholder="e.g. No longer working in hospitality." />
            </div>
            <div>
              <Label htmlFor="password">Current password</Label>
              <PasswordInput id="password" required autoComplete="current-password" minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none mt-1" />
              <p className="mt-1 text-xs text-muted-foreground">We re-verify your password to make sure this is really you.</p>
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-1 accent-accent" />
              <span>
                I understand that my personal data will be scrubbed, my account permanently locked, and that financial records will be retained for 6 years for tax purposes.
              </span>
            </label>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || !acknowledged}
                className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90 tracking-wide-luxury">
                {busy ? "Working…" : "Submit deletion request"}
              </Button>
              <Link to="/account" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent self-center">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
